'use client';

import { useState, useEffect } from 'react';
import FilterPanel from './FilterPanel';
import ProjectCarousel from './ProjectCarousel';
import ProjectCard from './ProjectCard';
import ProjectModal from './ProjectModal';
import StatsSection from './StatsSection';

interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  year: number;
  image: string;
  alt: string;
  description: string;
  challenges: string[];
  sustainability: {
    energySaved: string;
    co2Reduction: string;
    dpeImprovement: string;
  };
  clientName: string;
  clientTestimonial: string;
  videoUrl?: string;
}

interface FilterState {
  category: string;
  location: string;
  searchQuery: string;
}

interface PortfolioInteractiveProps {
  projects: Project[];
  categories: string[];
  locations: string[];
  stats: {
    projectsCompleted: number;
    satisfiedClients: number;
    yearsExperience: number;
    energySaved: string;
  };
}

const PortfolioInteractive = ({
  projects,
  categories,
  locations,
  stats,
}: PortfolioInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>(projects);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentFilters, setCurrentFilters] = useState<FilterState>({
    category: 'Tous',
    location: 'Tous',
    searchQuery: '',
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = [...projects];

    if (currentFilters.category !== 'Tous') {
      filtered = filtered.filter((p) => p.category === currentFilters.category);
    }

    if (currentFilters.location !== 'Tous') {
      filtered = filtered.filter((p) => p.location === currentFilters.location);
    }

    if (currentFilters.searchQuery) {
      const query = currentFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.location.toLowerCase().includes(query)
      );
    }

    setFilteredProjects(filtered);
  }, [currentFilters, projects, isHydrated]);

  const handleFilterChange = (filters: FilterState) => {
    setCurrentFilters(filters);
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:107:6" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="107" data-component-end-line="109" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:108:8" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="108" data-component-end-line="108" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20animate-pulse%22%7D" className="h-96 bg-muted animate-pulse" />
      </div>
    );
  }

  return (
    <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:114:4" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="114" data-component-end-line="170" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <ProjectCarousel data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:115:6" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="115" data-component-end-line="118" data-component-file="PortfolioInteractive.tsx" data-component-name="ProjectCarousel" data-component-content="%7B%22elementName%22%3A%22ProjectCarousel%22%7D"
        projects={projects.slice(0, 6)}
        onProjectClick={handleProjectClick}
      />

      <StatsSection data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:120:6" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="120" data-component-end-line="120" data-component-file="PortfolioInteractive.tsx" data-component-name="StatsSection" data-component-content="%7B%22elementName%22%3A%22StatsSection%22%7D" stats={stats} />

      <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:122:6" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="122" data-component-end-line="167" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
        <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:123:8" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="123" data-component-end-line="130" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h2 data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:124:10" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="124" data-component-end-line="126" data-component-file="PortfolioInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-4xl%20md%3Atext-5xl%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Explorez%20Notre%20Portfolio%22%7D" className="font-playfair font-bold text-4xl md:text-5xl text-text-primary mb-4">
            Explorez Notre Portfolio
          </h2>
          <p data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:127:10" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="127" data-component-end-line="129" data-component-file="PortfolioInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22D%C3%A9couvrez%20nos%20r%C3%A9alisations%20exceptionnelles%20%C3%A0%20travers%20diff%C3%A9rentes%20cat%C3%A9gories%20de%20projets.%20Chaque%20transformation%20raconte%20une%20histoire%20unique%20d'excellence%20et%20d'innovation.%22%7D" className="text-text-secondary text-lg max-w-3xl mx-auto">
            Découvrez nos réalisations exceptionnelles à travers différentes catégories de projets. Chaque transformation raconte une histoire unique d'excellence et d'innovation.
          </p>
        </div>

        <FilterPanel data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:132:8" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="132" data-component-end-line="136" data-component-file="PortfolioInteractive.tsx" data-component-name="FilterPanel" data-component-content="%7B%22elementName%22%3A%22FilterPanel%22%7D"
          onFilterChange={handleFilterChange}
          categories={categories}
          locations={locations}
        />

        {filteredProjects.length === 0 ? (
          <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:139:10" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="139" data-component-end-line="149" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-20%22%7D" className="text-center py-20">
            <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:140:12" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="140" data-component-end-line="142" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-32%20h-32%20mx-auto%20mb-6%20bg-muted%20rounded-full%20flex%20items-center%20justify-center%22%7D" className="w-32 h-32 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
              <span data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:141:14" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="141" data-component-end-line="141" data-component-file="PortfolioInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-6xl%22%2C%22textContent%22%3A%22%F0%9F%94%8D%22%7D" className="text-6xl">🔍</span>
            </div>
            <h3 data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:143:12" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="143" data-component-end-line="145" data-component-file="PortfolioInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-2xl%20text-text-primary%20mb-3%22%2C%22textContent%22%3A%22Aucun%20Projet%20Trouv%C3%A9%22%7D" className="font-playfair font-bold text-2xl text-text-primary mb-3">
              Aucun Projet Trouvé
            </h3>
            <p data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:146:12" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="146" data-component-end-line="148" data-component-file="PortfolioInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20mb-6%22%2C%22textContent%22%3A%22Essayez%20de%20modifier%20vos%20crit%C3%A8res%20de%20recherche%20pour%20voir%20plus%20de%20r%C3%A9sultats.%22%7D" className="text-text-secondary mb-6">
              Essayez de modifier vos critères de recherche pour voir plus de résultats.
            </p>
          </div>
        ) : (
          <>
            <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:152:12" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="152" data-component-end-line="154" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20text-text-secondary%20font-semibold%22%2C%22textContent%22%3A%22projet%20trouv%C3%A9%22%7D" className="mb-6 text-text-secondary font-semibold">
              {filteredProjects.length} projet{filteredProjects.length > 1 ? 's' : ''} trouvé{filteredProjects.length > 1 ? 's' : ''}
            </div>

            <div data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:156:12" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="156" data-component-end-line="164" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <ProjectCard data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:158:16" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="158" data-component-end-line="162" data-component-file="PortfolioInteractive.tsx" data-component-name="ProjectCard" data-component-content="%7B%22elementName%22%3A%22ProjectCard%22%7D"
                  key={project.id}
                  project={project}
                  onViewDetails={handleProjectClick}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <ProjectModal data-component-id="src/app/portfolio-showcase/components/PortfolioInteractive.tsx:169:6" data-component-path="src/app/portfolio-showcase/components/PortfolioInteractive.tsx" data-component-line="169" data-component-end-line="169" data-component-file="PortfolioInteractive.tsx" data-component-name="ProjectModal" data-component-content="%7B%22elementName%22%3A%22ProjectModal%22%7D" project={selectedProject} onClose={handleCloseModal} />
    </div>
  );
};

export default PortfolioInteractive;